import React, { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import api, { installationMessage } from "services/installation-api";
import { SHAPE, resolveGeometry, formatGallons } from "../capacity";
import { ChoiceCard, Field, SelectField, StepAlert, PrimaryButton, SecondaryButton, useInstallTheme } from "../ui";

export function AddressModeStep({ dispatch, onNext }) {
  const { t } = useInstallTheme();
  const choose = (mode) => {
    dispatch({ type: "SET_ADDRESS_MODE", mode });
    setTimeout(onNext, 0);
  };
  return (
    <>
      <ChoiceCard
        title="Use an existing service address"
        description="Pick a site already linked to this customer."
        onClick={() => choose("existing")}
        t={t}
      />
      <ChoiceCard
        title="Add a new service address"
        description="Enter the site where this monitor is being installed."
        onClick={() => choose("new")}
        t={t}
      />
    </>
  );
}

function addressLine(a) {
  const row = a?.attributes || a || {};
  const addr = row.Address || row.address || row;
  return [addr.street_address1, [addr.city, addr.state].filter(Boolean).join(", "), addr.postal_code]
    .filter(Boolean)
    .join(" ");
}

function addressIdOf(a) {
  const row = a?.attributes || a || {};
  return row.address_id ?? row.Address?.address_id ?? row.id;
}

export function AddressSelectStep({ addresses, selected, onSelect, t }) {
  return (
    <>
      {addresses.length === 0 ? (
        <StepAlert severity="info">
          No linked addresses yet. Go back and choose “Add a new service address”.
        </StepAlert>
      ) : (
        addresses.map((a) => {
          const id = addressIdOf(a);
          const row = a?.attributes || a || {};
          return (
            <ChoiceCard
              key={id}
              title={row.name || addressLine(a)}
              description={addressLine(a)}
              selected={selected && String(addressIdOf(selected)) === String(id)}
              onClick={() => onSelect(a)}
              t={t}
            />
          );
        })
      )}
    </>
  );
}

export function AddressCreateStep({ form, dropdowns, dispatch, t, darkMode, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const set = (patch) => dispatch({ type: "SET_ADDRESS_FORM", form: patch });

  const useLocation = useCallback(async () => {
    setLocating(true);
    setNotice("");
    setError("");
    try {
      if (!navigator.geolocation) throw new Error("Location is not available on this device.");
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 15000 }),
      );
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const suggestion = await api.reverseGeocode(lat, lng);
      if (!suggestion.supported) {
        set({ latitude: String(lat), longitude: String(lng) });
        setNotice("Location captured. Enter the address manually.");
      } else if (!suggestion.found) {
        set({ latitude: String(lat), longitude: String(lng) });
        setNotice("No address found for this location. Finish the form manually.");
      } else {
        set({
          street_address1: suggestion.street_address1 || form.street_address1,
          city: suggestion.city || form.city,
          state: suggestion.state || form.state,
          postal_code: suggestion.postal_code || form.postal_code,
          country: suggestion.country || form.country,
          latitude: String(lat),
          longitude: String(lng),
        });
        setNotice("Suggested address filled in. Review before saving.");
      }
    } catch (err) {
      setError(installationMessage(err));
    } finally {
      setLocating(false);
    }
  }, [form, set]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {notice && <StepAlert severity="info">{notice}</StepAlert>}
      {error && <StepAlert severity="error">{error}</StepAlert>}
      <SecondaryButton onClick={useLocation} disabled={locating || busy} t={t}>
        {locating ? "Finding location…" : "Use current location"}
      </SecondaryButton>
      <SelectField
        label="Address type"
        value={form.address_type_id}
        onChange={(e) => set({ address_type_id: e.target.value })}
        t={t}
        darkMode={darkMode}
      >
        {(dropdowns.addressTypes || []).map((row) => (
          <MenuItem key={row.address_type_id} value={String(row.address_type_id)}>
            {row.type_name || row.name}
          </MenuItem>
        ))}
      </SelectField>
      <Field label="Site name" value={form.name} onChange={(e) => set({ name: e.target.value })} t={t} darkMode={darkMode} helperText="Shown in the address list, e.g. “Home”." />
      <Field label="Street line 1" value={form.street_address1} onChange={(e) => set({ street_address1: e.target.value })} t={t} darkMode={darkMode} required />
      <Field label="Street line 2" value={form.street_address2} onChange={(e) => set({ street_address2: e.target.value })} t={t} darkMode={darkMode} />
      <Field label="City" value={form.city} onChange={(e) => set({ city: e.target.value })} t={t} darkMode={darkMode} required />
      <Field label="State" value={form.state} onChange={(e) => set({ state: e.target.value })} t={t} darkMode={darkMode} />
      <Field label="Postal code" value={form.postal_code} onChange={(e) => set({ postal_code: e.target.value })} t={t} darkMode={darkMode} inputMode="numeric" />
      <Field label="Country" value={form.country} onChange={(e) => set({ country: e.target.value })} t={t} darkMode={darkMode} />
      <SelectField
        label="Status"
        value={form.status_type_id}
        onChange={(e) => set({ status_type_id: e.target.value })}
        t={t}
        darkMode={darkMode}
      >
        {(dropdowns.statusOptions || dropdowns.statusTypes || []).map((row) => (
          <MenuItem key={row.status_type_id} value={String(row.status_type_id)}>
            {row.status_name || row.name}
          </MenuItem>
        ))}
      </SelectField>
      {onSaved ? null : null}
    </Box>
  );
}

export function AssetModeStep({ dispatch, onNext }) {
  const { t } = useInstallTheme();
  const choose = (mode) => {
    dispatch({ type: "SET_ASSET_MODE", mode });
    setTimeout(onNext, 0);
  };
  return (
    <>
      <ChoiceCard title="Use existing asset" description="This tank is already in Rivio." onClick={() => choose("existing")} t={t} />
      <ChoiceCard title="Add new asset" description="Register a new tank or asset at this address." onClick={() => choose("new")} t={t} />
    </>
  );
}

export function AssetSelectStep({ assets, selected, onSelect, t }) {
  const rows = (assets || []).filter((a) => a.address_id != null);
  return (
    <>
      {rows.length === 0 ? (
        <StepAlert severity="info">No assets at this address. Go back and choose “Add new asset”.</StepAlert>
      ) : (
        rows.map((asset) => {
          const a = asset.attributes || asset;
          return (
            <ChoiceCard
              key={a.address_item_id}
              title={a.map_identifier || a.name || `Asset ${a.address_item_id}`}
              description={[a.item_name || a.Item?.name, a.status_name].filter(Boolean).join(" · ")}
              selected={selected && String(selected.address_item_id) === String(a.address_item_id)}
              onClick={() => onSelect(asset)}
              t={t}
            />
          );
        })
      )}
    </>
  );
}

const SHAPE_OPTIONS = [
  { value: SHAPE.RECTANGLE, label: "Rectangular" },
  { value: SHAPE.SQUARE, label: "Square" },
  { value: SHAPE.CYLINDER_VERTICAL, label: "Round, upright" },
  { value: SHAPE.CYLINDER_HORIZONTAL, label: "Round, on its side" },
];

export function TankCapacityCalculator({ form, dispatch, t, darkMode }) {
  const geometry = resolveGeometry({
    shape: form.shape,
    mode: form.capacity_mode,
    dims: form.dims,
    capacity_gallons: form.capacity_gallons,
    max_fill_pct: form.max_fill_pct,
    ratio: form.ratio,
  });
  const setDim = (key, value) =>
    dispatch({
      type: "SET_ASSET_FORM",
      form: { dims: { ...form.dims, [key]: value } },
    });
  const set = (patch) => dispatch({ type: "SET_ASSET_FORM", form: patch });
  const dims = geometry.values;
  const showLength = form.shape === SHAPE.RECTANGLE || form.shape === SHAPE.SQUARE || form.shape === SHAPE.CYLINDER_HORIZONTAL;
  const showWidth = form.shape === SHAPE.RECTANGLE;
  const showDiameter = form.shape === SHAPE.CYLINDER_VERTICAL || form.shape === SHAPE.CYLINDER_HORIZONTAL;
  const showDepth = form.shape !== SHAPE.CYLINDER_HORIZONTAL;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SelectField
        label="Tank shape"
        value={form.shape}
        onChange={(e) => set({ shape: e.target.value })}
        t={t}
        darkMode={darkMode}
      >
        {SHAPE_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </SelectField>
      <Box display="flex" gap={1}>
        <SecondaryButton
          t={t}
          disabled={form.capacity_mode === "measurements"}
          onClick={() => set({ capacity_mode: "measurements" })}
        >
          Calculate from measurements
        </SecondaryButton>
        <SecondaryButton
          t={t}
          disabled={form.capacity_mode === "known"}
          onClick={() => set({ capacity_mode: "known" })}
        >
          I know the capacity
        </SecondaryButton>
      </Box>
      {showLength && (
        <Field
          label={form.shape === SHAPE.CYLINDER_HORIZONTAL ? "Length" : "Length"}
          value={dims.length_in}
          onChange={(e) => setDim("length_in", e.target.value)}
          inputMode="decimal"
          t={t}
          darkMode={darkMode}
          helperText={geometry.sources?.length_in === "calculated" ? "Estimated from capacity." : undefined}
        />
      )}
      {showWidth && (
        <Field
          label="Width"
          value={dims.width_in}
          onChange={(e) => setDim("width_in", e.target.value)}
          inputMode="decimal"
          t={t}
          darkMode={darkMode}
        />
      )}
      {showDiameter && (
        <Field
          label="Diameter"
          value={dims.diameter_in}
          onChange={(e) => setDim("diameter_in", e.target.value)}
          inputMode="decimal"
          t={t}
          darkMode={darkMode}
          helperText={geometry.sources?.diameter_in === "calculated" ? "Estimated from capacity." : undefined}
        />
      )}
      {showDepth && (
        <Field
          label="Inside depth"
          value={dims.depth_in}
          onChange={(e) => setDim("depth_in", e.target.value)}
          inputMode="decimal"
          t={t}
          darkMode={darkMode}
          helperText="Inside tank depth, not the current liquid level."
        />
      )}
      {form.capacity_mode === "known" && (
        <Field
          label="Known capacity (US gallons)"
          value={form.capacity_gallons}
          onChange={(e) => set({ capacity_gallons: e.target.value })}
          inputMode="decimal"
          t={t}
          darkMode={darkMode}
          helperText="Rated capacity from the tank label when available."
        />
      )}
      {form.shape === SHAPE.RECTANGLE && form.capacity_mode === "known" && (
        <Field
          label="Length:width ratio"
          value={form.ratio}
          onChange={(e) => set({ ratio: e.target.value })}
          inputMode="decimal"
          t={t}
          darkMode={darkMode}
          helperText="Optional. Leave blank for a square footprint."
        />
      )}
      <Field
        label="Max fill level (%)"
        value={form.max_fill_pct}
        onChange={(e) => set({ max_fill_pct: e.target.value })}
        inputMode="decimal"
        t={t}
        darkMode={darkMode}
        helperText="Optional operating fill limit."
      />
      {Object.keys(geometry.errors).length > 0 && (
        <StepAlert severity="info">
          Enter measurements to see capacity. Use inside measurements when available — do not enter a tank to take them.
        </StepAlert>
      )}
      <Box p={2} borderRadius="16px" sx={{ background: t.softSurface, border: `1px solid ${t.borderSubtle}` }}>
        <Typography variant="caption" sx={{ color: t.textMuted }}>
          {geometry.estimated ? "Estimated capacity" : "Capacity"}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {dims.capacity_gallons === "" ? "—" : `${formatGallons(dims.capacity_gallons)} gal`}
        </Typography>
        {dims.usable_capacity_gallons !== "" && (
          <Typography variant="body2" sx={{ color: t.textMuted }}>
            Usable: {formatGallons(dims.usable_capacity_gallons)} gal
          </Typography>
        )}
        <Typography variant="caption" display="block" mt={0.5} sx={{ color: t.textMuted }}>
          Calculated capacity is an estimate. Confirm the rated capacity when available.
        </Typography>
      </Box>
    </Box>
  );
}

export function AssetCreateStep({ form, dropdowns, addressId, targets, dispatch, t, darkMode }) {
  const set = (patch) => dispatch({ type: "SET_ASSET_FORM", form: patch });
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Field label="Asset name" value={form.name} onChange={(e) => set({ name: e.target.value })} t={t} darkMode={darkMode} required />
      <Field
        label="Map/field identifier"
        value={form.map_identifier}
        onChange={(e) => set({ map_identifier: e.target.value })}
        t={t}
        darkMode={darkMode}
        helperText="Sticker or location code on the tank."
      />
      <SelectField label="Asset type" value={form.item_id} onChange={(e) => set({ item_id: e.target.value })} t={t} darkMode={darkMode} required>
        {(dropdowns.items || []).map((row) => (
          <MenuItem key={row.item_id ?? row.id} value={String(row.item_id ?? row.id)}>
            {row.name || row.item_name}
          </MenuItem>
        ))}
      </SelectField>
      <SelectField label="Status" value={form.status_type_id} onChange={(e) => set({ status_type_id: e.target.value })} t={t} darkMode={darkMode}>
        {(dropdowns.statusOptions || dropdowns.statusTypes || []).map((row) => (
          <MenuItem key={row.status_type_id} value={String(row.status_type_id)}>
            {row.status_name || row.name}
          </MenuItem>
        ))}
      </SelectField>
      <TankCapacityCalculator form={form} dispatch={dispatch} t={t} darkMode={darkMode} />
      {addressId == null && <StepAlert severity="warning">Select a service address first.</StepAlert>}
      {targets == null ? null : null}
    </Box>
  );
}
